// ─────────────────────────────────────────────
//  breakWaiting — everyone who is waiting for a break to START, from BOTH
//  records that can put them there.
//
//  ⚠️ WHY THIS EXISTS. "Waiting for a break" had two halves living in two
//  places, and only one of them was ever counted:
//
//    • the ASK      — `requestMyBreak` raises a BREAK_REQUEST alert and writes
//                     nothing to the room doc (useRoomBreakActions).
//    • the NAMING   — `assignCoverToRoom` writes `breaks.<ch> = "assigned"` on
//                     the room doc and raises no alert at all.
//
//  The BREAKS WAITING strip read the alert bus alone. Live board, 2026-09-13 at
//  1:08 PM: WOR 7 held `breaks: {breakfast: "assigned"}`, reliever Diane Choi,
//  `onBreak: false` — Jerod Hudson with a cover named and nobody in the room —
//  and the strip beside it read `0 · Nobody is waiting for a break.` Not a
//  stale count: no request had EVER been raised, because the AIC had named the
//  cover proactively, which is the ordinary way it happens. Zero live requests
//  was a true answer to a question nobody had asked.
//
//  ⚠️ AND `assigned` IS THE STATE THE BOARD ITSELF CALLS THE URGENT ONE. It
//  owns the only dark tile on the breaks card precisely because "named but
//  NOBODY IN THE ROOM" is what most needs noticing (domain/breaks BREAK_STATES).
//  A summary that omits it is worse than no summary: the strip does not merely
//  fail to mention Jerod, it asserts that nobody like him exists.
//
//  Product owner, 2026-09-13: "as soon [as] you assigned a break and the
//  assigned is not there taking over the break they be show in break waiting,
//  when you requested the break they should be show there too." One queue.
//
//  ⚠️ ONE ROW PER ROOM, NOT ONE PER RECORD. `coverBreak` assigns the channel and
//  deliberately leaves the request alert LIVE until the cover arrives, so a room
//  can legitimately hold both records at once. Counting both would report two
//  people waiting where one is.
//
//  Pure: no Firebase, no React, no clock of its own — `nowMs`/`nowMin` are
//  parameters so a board on another day, or one whose clock is withheld,
//  reports "age unknown" rather than inventing an age.
// ─────────────────────────────────────────────
import { ALERT_TYPES, isLiveAlert, alertSentAt } from "./alerts";
import { BREAK_CHANNELS, getChannelState } from "./breaks";
import { alertQueue } from "./controlPanel";
import { roomForOrRoom } from "./gridTemplate";
import { hmToMinutes } from "./timeline";

// The channel somebody has been named for and has not yet arrived on, or null.
//
// `isCoverPending` answers the yes/no; this returns WHICH, because the row has
// to name the break and read its stamp. First match wins — a provider with two
// channels assigned at once is waiting once, not twice.
export function pendingCoverChannel(breaks) {
  return BREAK_CHANNELS.find((ch) => getChannelState(breaks, ch) === "assigned") || null;
}

// Minutes since an "HH:MM" stamp, on the board's own clock.
//
// ⚠️ NULL, NEVER A NEGATIVE. A stamp in the future is a clock problem (a device
// in another timezone, a board rolled past midnight), and the strip already
// prints "age unknown" for null — which is true. "-6 min" is not, and a
// negative sorts to the head of a queue about who has waited longest.
function minutesSinceHm(hm, nowMin) {
  if (!Number.isFinite(nowMin)) return null;
  const since = hmToMinutes(hm);
  if (since == null) return null;
  const waited = nowMin - since;
  return waited >= 0 ? waited : null;
}

function minutesSinceAlert(alert, nowMs) {
  if (!Number.isFinite(nowMs)) return null;
  const d = alertSentAt(alert);
  const ms = d instanceof Date ? d.getTime() : null;
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.round((nowMs - ms) / 60000));
}

/**
 * Everyone waiting for a break to start, oldest first.
 *
 * @param {object}  ctx
 * @param {Array}   ctx.alerts       the live alert bus (filtered here to requests)
 * @param {Array}   ctx.rooms        room docs — the ONLY place `assigned` lives
 * @param {Array}   ctx.assignments  today's rows, to name a provider the room doc omits
 * @param {number}  ctx.nowMs        real instant, for alert ages
 * @param {number}  ctx.nowMin       minutes past midnight, for `breakTimes` stamps
 * @returns {Array} [{ kind, alert, roomId, roomName, room, assignment,
 *                     providerName, breakType, relieverName, waitedMin }]
 */
export function breakWaitingRows({
  alerts = [],
  rooms = [],
  assignments = [],
  nowMs = null,
  nowMin = null,
} = {}) {
  const roomList = Array.isArray(rooms) ? rooms : [];
  const roomById = new Map(roomList.map((r) => [r && r.id, r]).filter(([id]) => id));

  const asgByRoomId = new Map();
  for (const a of Array.isArray(assignments) ? assignments : []) {
    if (!a || !a.orRoom) continue;
    const room = roomForOrRoom(a.orRoom, roomList);
    if (room && !asgByRoomId.has(room.id)) asgByRoomId.set(room.id, a);
  }

  // ── The ASK ────────────────────────────────────────────────────────────────
  // Ordering is `alertQueue`: OLDEST FIRST, with a null `sentAt` — a
  // serverTimestamp that has not round-tripped on the requesting device —
  // treated as age 0 and therefore sorted LAST, never first.
  const live = (Array.isArray(alerts) ? alerts : []).filter(
    (a) => a && a.type === ALERT_TYPES.BREAK_REQUEST && isLiveAlert(a)
  );
  const byRoom = new Map();
  for (const a of alertQueue(live, Number.isFinite(nowMs) ? nowMs : Date.now())) {
    const room = roomById.get(a.roomId) || null;
    const assignment = room ? asgByRoomId.get(room.id) || null : null;
    // A request with no resolvable room still has to be nameable, so it is keyed
    // by whatever identity it carries rather than dropped.
    const key = a.roomId || `alert:${a.id}`;
    if (byRoom.has(key)) continue; // oldest request wins; one row per room
    byRoom.set(key, {
      kind: "requested",
      alert: a,
      roomId: a.roomId || null,
      // The alert carries the room name for rooms the board cannot resolve — an
      // alarm with no matching room doc still has to be nameable.
      roomName: room?.name || a.roomName || "",
      room,
      assignment,
      providerName: a.giverName || assignment?.ahpName || room?.providerName || "",
      breakType: a.breakType || "",
      relieverName: "",
      waitedMin: minutesSinceAlert(a, nowMs),
    });
  }

  // ── The NAMING ─────────────────────────────────────────────────────────────
  for (const room of roomList) {
    if (!room || !room.id) continue;
    const channel = pendingCoverChannel(room.breaks || {});
    if (!channel) continue;
    const assignment = asgByRoomId.get(room.id) || null;
    const prior = byRoom.get(room.id) || null;
    byRoom.set(room.id, {
      // `assigned` OUTRANKS a request on the same room: somebody has been found,
      // and the strip says so rather than asking for a second one.
      kind: "assigned",
      // ⚠️ THE ALERT RIDES ALONG. Withdrawing the request is still the AIC's to
      // do, and a row with no alert offers no way to do it.
      alert: prior?.alert || null,
      roomId: room.id,
      roomName: room.name || prior?.roomName || "",
      room,
      assignment,
      providerName:
        room.providerName || assignment?.ahpName || prior?.providerName || "",
      // The channel that was NAMED, which is the break actually being waited on.
      breakType: channel,
      relieverName: room.relieverName || "",
      // ⚠️ THE WAIT STARTED WHEN THEY ASKED, not when somebody was named. Where
      // both records exist the request is the older and truer clock; naming a
      // cover must never reset a provider's age to zero.
      waitedMin:
        prior?.waitedMin ??
        minutesSinceHm((room.breakTimes || {})[channel], nowMin),
    });
  }

  // Oldest first across both kinds. An unknown age sorts LAST, never first —
  // same rule as `alertQueue`, for the same reason.
  return [...byRoom.values()]
    .map((r, i) => ({ r, i }))
    .sort((x, y) => (y.r.waitedMin ?? 0) - (x.r.waitedMin ?? 0) || x.i - y.i)
    .map((x) => x.r);
}

export default breakWaitingRows;
